/**
 * SignupWizard - 3-step SMS-first registration
 *
 * Step 1: phone + password (+ optional email) → SMS OTP sent
 * Step 2: enter 6-digit code (with resend + "send to email instead" fallback)
 * Step 3: complete profile (name, DOB, country, address) → account created + JWT
 *
 * Backend endpoints: /api/auth/signup/{start,verify-otp,resend,switch-to-email,complete-profile}
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertCircle, Loader2, Phone, Mail, ArrowLeft, ShieldCheck, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StepIndicator = ({ step }) => (
  <div className="flex items-center justify-center gap-2 mb-6" data-testid="signup-step-indicator">
    {[1, 2, 3].map((n) => (
      <div key={n} className="flex items-center gap-2">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 ${
            step === n
              ? 'bg-blue-600 text-white border-blue-600'
              : step > n
              ? 'bg-green-500 text-white border-green-500'
              : 'bg-white text-gray-400 border-gray-300'
          }`}
        >
          {step > n ? <CheckCircle2 className="w-4 h-4" /> : n}
        </div>
        {n < 3 && <div className={`w-8 h-0.5 ${step > n ? 'bg-green-500' : 'bg-gray-300'}`} />}
      </div>
    ))}
  </div>
);

const StepStart = ({ form, setForm, onNext, error, loading }) => (
  <>
    <CardHeader>
      <CardTitle className="text-2xl flex items-center gap-2">
        <Phone className="w-6 h-6 text-blue-600" /> Create your Zeilian account
      </CardTitle>
      <CardDescription>
        We'll send a verification code to your phone. Email is optional - you can add it later.
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div>
        <Label htmlFor="phone">Mobile phone (international format)</Label>
        <Input
          id="phone"
          type="tel"
          placeholder="+44 7700 900123"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          data-testid="signup-phone"
        />
        <p className="text-xs text-gray-500 mt-1">Include the country code, starting with +</p>
      </div>
      <div>
        <Label htmlFor="email">Email <span className="text-gray-400 text-xs">(optional)</span></Label>
        <Input
          id="email"
          type="email"
          placeholder="you@example.com"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="signup-email"
        />
      </div>
      <div>
        <Label htmlFor="country">Country</Label>
        <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
          <SelectTrigger data-testid="signup-country">
            <SelectValue placeholder="Choose your country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="UK">United Kingdom</SelectItem>
            <SelectItem value="LK">Sri Lanka</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="At least 8 characters"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          data-testid="signup-password"
        />
      </div>
      {error && (
        <Alert variant="destructive" data-testid="signup-error">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button
        onClick={onNext}
        disabled={loading || !form.phone || !form.password || !form.country}
        className="w-full"
        data-testid="signup-send-code-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Send verification code
      </Button>
      <p className="text-center text-sm text-gray-600">
        Already have an account? <Link to="/login" className="text-blue-600 hover:underline">Sign in</Link>
      </p>
    </CardContent>
  </>
);

const StepOtp = ({ form, setForm, session, otpCode, setOtpCode, onVerify, onResend, onSwitchToEmail, onBack, error, loading, cooldown }) => {
  const [emailFallback, setEmailFallback] = useState('');
  const [showEmailFallback, setShowEmailFallback] = useState(false);
  return (
    <>
      <CardHeader>
        <CardTitle className="text-2xl">Enter the 6-digit code</CardTitle>
        <CardDescription>
          Sent to <span className="font-semibold">{session?.masked_target}</span> via {session?.channel === 'sms' ? 'SMS' : 'email'}.
          {session?.delivery_status === 'failed' && (
            <span className="block mt-2 text-amber-600 text-sm">
              ⚠ SMS delivery to this number failed. You can try again or switch to email below.
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="otp">Verification code</Label>
          <Input
            id="otp"
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="123456"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="text-center text-2xl tracking-widest"
            data-testid="signup-otp-input"
          />
        </div>
        {error && (
          <Alert variant="destructive" data-testid="signup-otp-error">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <Button
          onClick={onVerify}
          disabled={loading || otpCode.length < 4}
          className="w-full"
          data-testid="signup-verify-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Verify and continue
        </Button>
        <div className="flex justify-between text-sm">
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="signup-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onResend}
            disabled={cooldown > 0}
            data-testid="signup-resend-btn"
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
          </Button>
        </div>
        <div className="pt-2 border-t">
          {!showEmailFallback ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setShowEmailFallback(true)}
              data-testid="signup-switch-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" /> SMS not arriving? Send code by email instead
            </Button>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="email-fallback">Email address</Label>
              <Input
                id="email-fallback"
                type="email"
                placeholder="you@example.com"
                value={emailFallback}
                onChange={(e) => setEmailFallback(e.target.value)}
                data-testid="signup-email-fallback"
              />
              <Button
                size="sm"
                className="w-full"
                disabled={!emailFallback.includes('@')}
                onClick={() => onSwitchToEmail(emailFallback)}
                data-testid="signup-email-fallback-btn"
              >
                Send code to this email
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </>
  );
};

const StepProfile = ({ form, setForm, onSubmit, error, loading }) => (
  <>
    <CardHeader>
      <CardTitle className="text-2xl flex items-center gap-2">
        <ShieldCheck className="w-6 h-6 text-green-600" /> Complete your profile
      </CardTitle>
      <CardDescription>Just a few more details to finish setting up your account.</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div>
        <Label htmlFor="name">Full name</Label>
        <Input
          id="name"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="signup-name"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="dob">Date of birth</Label>
          <Input
            id="dob"
            type="date"
            value={form.dob}
            onChange={(e) => setForm({ ...form, dob: e.target.value })}
            data-testid="signup-dob"
          />
        </div>
        <div>
          <Label htmlFor="account_type">Account type</Label>
          <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
            <SelectTrigger data-testid="signup-account-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="buyer">Buyer only</SelectItem>
              <SelectItem value="seller">Seller only</SelectItem>
              <SelectItem value="both">Buyer and Seller</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label htmlFor="address">Address <span className="text-gray-400 text-xs">(optional)</span></Label>
        <Input
          id="address"
          placeholder="Street, City, Postcode"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          data-testid="signup-address"
        />
      </div>
      {error && (
        <Alert variant="destructive" data-testid="signup-profile-error">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Button
        onClick={onSubmit}
        disabled={loading || !form.name}
        className="w-full"
        data-testid="signup-complete-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
        Create my account
      </Button>
    </CardContent>
  </>
);

export default function SignupWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    phone: '', email: '', password: '', country: '',
    name: '', dob: '', address: '', account_type: 'both',
  });
  const [session, setSession] = useState(null);
  const [pendingToken, setPendingToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const err = (e) => {
    const d = e?.response?.data?.detail;
    return typeof d === 'string' ? d : d?.message || 'Something went wrong. Please try again.';
  };

  const startCooldown = (sec = 60) => {
    setCooldown(sec);
    const iv = setInterval(() => setCooldown((s) => (s <= 1 ? (clearInterval(iv), 0) : s - 1)), 1000);
  };

  const handleStart = async () => {
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/signup/start`, {
        phone: form.phone.trim(), email: form.email.trim() || null,
        password: form.password, country: form.country,
      });
      setSession(data);
      setStep(2);
      startCooldown(60);
    } catch (e) { setError(err(e)); }
    setLoading(false);
  };

  const handleVerify = async () => {
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/signup/verify-otp`, {
        session_id: session.session_id, code: otpCode,
      });
      setPendingToken(data.pending_token);
      setStep(3);
    } catch (e) { setError(err(e)); }
    setLoading(false);
  };

  const handleResend = async () => {
    setError('');
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/signup/resend`, {
        session_id: session.session_id,
      });
      startCooldown(60);
      setSession({ ...session, masked_target: data.masked_target || session.masked_target, delivery_status: data.success ? 'sent' : 'failed' });
    } catch (e) { setError(err(e)); }
  };

  const handleSwitchToEmail = async (email) => {
    setError('');
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/signup/switch-to-email`, {
        session_id: session.session_id, email,
      });
      setSession({ ...session, channel: 'email', masked_target: data.masked_target, delivery_status: 'sent' });
      startCooldown(60);
    } catch (e) { setError(err(e)); }
  };

  const handleComplete = async () => {
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/signup/complete-profile`, {
        pending_token: pendingToken,
        name: form.name,
        dob: form.dob || null,
        country: form.country,
        address: form.address || null,
        account_type: form.account_type,
      });
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/');
      window.location.reload();
    } catch (e) { setError(err(e)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-start justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <StepIndicator step={step} />
        <Card data-testid="signup-wizard-card">
          {step === 1 && <StepStart form={form} setForm={setForm} onNext={handleStart} error={error} loading={loading} />}
          {step === 2 && (
            <StepOtp
              form={form} setForm={setForm}
              session={session}
              otpCode={otpCode} setOtpCode={setOtpCode}
              onVerify={handleVerify} onResend={handleResend}
              onSwitchToEmail={handleSwitchToEmail}
              onBack={() => setStep(1)}
              error={error} loading={loading} cooldown={cooldown}
            />
          )}
          {step === 3 && (
            <StepProfile form={form} setForm={setForm} onSubmit={handleComplete} error={error} loading={loading} />
          )}
        </Card>
      </div>
    </div>
  );
}
